import React, { useCallback, useMemo } from 'react';
import { PRODUCT_TYPES } from '../../../../constants/enums.js';

export const usePanelProcessing = (orderlinesData, campaignType) => {
    const processPlayPanels = useCallback((playData) => {
        return (playData?.contacts?.panels || []).sort((a, b) => a.panelId.localeCompare(b.panelId));
    }, []);

    const groupPanelsByPanelId = useCallback((panels) => {
        return panels.reduce((acc, panel) => {
            if (!acc[panel.panelId]) {
                acc[panel.panelId] = [];
            }
            acc[panel.panelId].push(panel);
            return acc;
        }, {});
    }, []);

    const createPanelObject = useCallback((panelId, panelsGroup) => {
        const firstPanel = panelsGroup[0];
        const isSinglePanel = panelsGroup.length === 1;
;        // Extract properties from firstPanel
        const {
            environment,
            address,
            city,
            postalCode,
            plannedDate,
            region,
            latitude,
            longitude,
            debugInfo,
            county
        } = firstPanel;

        const baseObject = {
            panelId,
            panels: panelsGroup,
            environment,
            address,
            city,
            postalCode,
            region,
            latitude,
            longitude,
            debugInfo,
            county
        };

        if (isSinglePanel) {
            const { timeStamp, design, status, postingImageUrls } = firstPanel;
            return {
                ...baseObject,
                timeStamp,
                plannedDate,
                design,
                status,
                postingImageUrls
            };
        }

        return baseObject;
    }, []);

    const processPostingPanels = useCallback((postingData) => {
        // Make sure we only flatten panels once
        const allPanels = postingData.flatMap(item => item.panels);

        const groupedPanels = groupPanelsByPanelId(allPanels);

        const processedPanels = Object.entries(groupedPanels)
            .map(([panelId, group]) => createPanelObject(panelId, group))
            .sort((a, b) => {
                // First sort by image presence
                const aHasImage = a.panels.some(panel => panel.postingImageUrls?.length);
                const bHasImage = b.panels.some(panel => panel.postingImageUrls?.length);

                if (bHasImage !== aHasImage) {
                    return bHasImage - aHasImage; // Panels with images come first
                }

                // If image presence is the same, sort by panelId
                return a.panelId.localeCompare(b.panelId);
            });
        return processedPanels;
    }, [groupPanelsByPanelId, createPanelObject]);

    const panels = useMemo(() => {
        if (!orderlinesData) return [];

        return campaignType === PRODUCT_TYPES.PLAY
            ? processPlayPanels(orderlinesData.playData)
            : processPostingPanels(orderlinesData.postingData);
    }, [campaignType, orderlinesData, processPlayPanels, processPostingPanels]);

    return { panels: panels || [] };
};